/**
 * Seeded, tileable value noise — the grain in the table's wood, the veins in
 * the board's stone, the foxing on the map's parchment.
 *
 * Seeded so the table looks the same every time it is drawn (and nothing here
 * reaches for `Math.random`, which the whole repository forbids). Tileable so a
 * texture can repeat across a large surface without a visible seam: the lattice
 * wraps at `period`, and every octave's period is a multiple of the first.
 */

/** A hash of a lattice point to [0, 1). */
function hash(seed: number, x: number, y: number): number {
  let h = seed ^ Math.imul(x, 0x27d4eb2d) ^ Math.imul(y, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const smooth = (t: number): number => t * t * (3 - 2 * t);
const wrap = (n: number, period: number): number => ((n % period) + period) % period;

/**
 * Value noise at (x, y) on a lattice that repeats every `px` by `py` cells.
 * Returns [0, 1).
 */
export function valueNoise(seed: number, x: number, y: number, px: number, py: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = smooth(x - x0);
  const fy = smooth(y - y0);
  const a = hash(seed, wrap(x0, px), wrap(y0, py));
  const b = hash(seed, wrap(x0 + 1, px), wrap(y0, py));
  const c = hash(seed, wrap(x0, px), wrap(y0 + 1, py));
  const d = hash(seed, wrap(x0 + 1, px), wrap(y0 + 1, py));
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
}

/**
 * Fractal noise: `octaves` layers of value noise, each twice as fine and half as
 * strong. `u`, `v` are in [0, 1) across the texture; `cx`, `cy` are how many
 * lattice cells the first octave spans in each direction — unequal values
 * stretch the pattern, which is how grain and banding are made.
 */
export function fbm(
  seed: number,
  u: number,
  v: number,
  cx: number,
  cy: number,
  octaves: number,
): number {
  let sum = 0;
  let amp = 0.5;
  let norm = 0;
  let fx = cx;
  let fy = cy;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise(seed + o * 1013, u * fx, v * fy, fx, fy);
    norm += amp;
    amp *= 0.5;
    fx *= 2;
    fy *= 2;
  }
  return sum / norm;
}

/** A small seeded generator for scattering things (specks, fibres): mulberry32. */
export function scatter(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
